import { PageNameContext } from '@contexts';
import { CourseHeaderContainer } from '@features/course/courseHeader';
import { CourseContainer } from '@features/course/course.container';
import { DiscountProgramFaviconContainer } from '@features/common/discountProgram/discountProgramFavicon';
import { PageScrollArrowContainer } from '@features/common/pageScrollArrow/pageScrollArrow.container';
import {
  BackgroundContainer,
  CanonicalContainer,
  CatalogPageTypes,
  clearAcademikaCourseVendor,
  CookieNotificationContextProvider,
  DiscountProgramContext,
  FastLinksContext,
  FasttrackWidgetContainer,
  getCoursePageEventData,
  getTranslationsByCompany,
  HeadOrganizationMetaTags,
  HeadProductMetaTags,
  HeadSeoMetaTagsContainer,
  mindboxService,
  PageStatusCodes,
  PageWrapperContainer,
  PromoCodeContextProvider,
  RegisterPopupContextProvider,
  TranslationsContext,
  useViewPageDataLayer,
} from '@lerna-core';
import {
  CourseContext,
  courseDataServerRequest,
  CourseFooterContainer,
  CoursePageContext,
  CoursePageModel,
  getProductDataLayer,
} from '@features/course';
import { wrapper } from '@store/store-v2';
import { GetServerSidePropsResult } from 'next';
import React, { ReactElement, useEffect } from 'react';
import { getBackgroundColors } from '@styles';

const CoursePageDefault = (props: CoursePageModel): ReactElement => {
  useViewPageDataLayer(
    CatalogPageTypes.coursePage,
    getCoursePageEventData(
      CatalogPageTypes.coursePage,
      getProductDataLayer(props.course, props.vendors)
    )
  );

  useEffect(() => {
    mindboxService.sendEvent(
      mindboxService.EventName.gotToCourse,
      mindboxService.getGoToCoursePayload(props.course.numericId)
    );
  }, []);

  const Header = <CourseHeaderContainer />;
  const Content = <CourseContainer />;
  const Footer = <CourseFooterContainer />;

  return (
    <PageNameContext.Provider value={CatalogPageTypes.coursePage}>
      <PromoCodeContextProvider props={props.promoCode}>
        <FastLinksContext.Provider value={props.fastLinks}>
          <DiscountProgramContext.Provider value={props.discountProgram}>
            <TranslationsContext.Provider value={props.translations}>
              <CourseContext.Provider value={props.course}>
                <RegisterPopupContextProvider
                  departments={props.departments}
                  positions={props.positions}
                  currencies={props.currencies}
                  canClose
                  canShowWelcomePopup
                >
                  <CookieNotificationContextProvider>
                    <CoursePageContext.Provider value={props}>
                      <BackgroundContainer {...getBackgroundColors()} />
                      <DiscountProgramFaviconContainer
                        favicon={props?.discountProgram?.favicon}
                      />
                      <HeadSeoMetaTagsContainer seo={props.seo} />
                      <HeadProductMetaTags course={props.course} />
                      <HeadOrganizationMetaTags />
                      <CanonicalContainer hrefWithWWW />
                      <PageWrapperContainer
                        header={Header}
                        content={Content}
                        footer={Footer}
                      />
                      <FasttrackWidgetContainer />
                      <PageScrollArrowContainer />
                    </CoursePageContext.Provider>
                  </CookieNotificationContextProvider>
                </RegisterPopupContextProvider>
              </CourseContext.Provider>
            </TranslationsContext.Provider>
          </DiscountProgramContext.Provider>
        </FastLinksContext.Provider>
      </PromoCodeContextProvider>
    </PageNameContext.Provider>
  );
};

const CoursePage = (props: CoursePageModel): ReactElement => (
  <CoursePageDefault {...props} />
);

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async (context): Promise<GetServerSidePropsResult<CoursePageModel>> => {
      const courseData = await courseDataServerRequest(store, context);

      if (
        !courseData ||
        courseData.statusCode === PageStatusCodes.errorNotFound
      ) {
        return {
          notFound: true,
        };
      }

      return {
        props: {
          ...courseData,
          ...getTranslationsByCompany(courseData.user?.career?.companyId),
          course: clearAcademikaCourseVendor(courseData.course),
        },
      };
    }
);

export default CoursePage;
